import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isLandingOnly } from '../utils/buildMode';
import { Button } from './shadcn/button';
import { Home, Search, AlertTriangle, ArrowRight, LogIn, UserPlus, Mail } from 'lucide-react';

const NotFound = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animations after component mounts
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-10 left-10 w-64 h-64 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
        <div className="absolute top-10 right-10 w-64 h-64 bg-indigo-300 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-10 left-1/2 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
      </div>
      
      <div className={`max-w-md w-full text-center relative z-10 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        <div className={`mb-8 transition-all duration-500 delay-150 ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}>
          <div className="mx-auto bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full p-4 w-24 h-24 shadow-lg flex items-center justify-center transform hover:scale-110 transition-transform duration-300">
            <AlertTriangle className="h-12 w-12 text-white" />
          </div>
        </div>
        
        <h1 className={`text-5xl font-bold text-gray-900 mb-4 transition-all duration-500 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          404
        </h1>
        <h2 className={`text-2xl font-semibold text-gray-800 mb-4 transition-all duration-500 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          Page Not Found
        </h2>
        
        <p className={`text-gray-600 mb-8 transition-all duration-500 delay-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          Sorry, we couldn't find the page you're looking for. The page may have been moved, deleted, or the URL might be incorrect.
        </p>
        
        <div className={`flex flex-col sm:flex-row gap-4 justify-center mb-8 transition-all duration-500 delay-900 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <Button 
            onClick={() => navigate('/')}
            className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white transform hover:-translate-y-1 transition duration-300"
          >
            <Home className="mr-2 h-4 w-4" />
            Back to Home
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          
          {currentUser ? (
            <Button 
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50 transform hover:-translate-y-1 transition duration-300"
            >
              <Search className="mr-2 h-4 w-4" />
              Dashboard
            </Button>
          ) : isLandingOnly ? (
            <Button 
              onClick={() => navigate('/contact-us')}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white transform hover:-translate-y-1 transition duration-300"
            >
              <Mail className="mr-2 h-4 w-4" />
              Contact Us
            </Button>
          ) : (
            <>
              <Button 
                onClick={() => navigate('/login')}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50 transform hover:-translate-y-1 transition duration-300"
              >
                <LogIn className="mr-2 h-4 w-4" />
                Login
              </Button>
              <Button 
                onClick={() => navigate('/register')}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white transform hover:-translate-y-1 transition duration-300"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Register
              </Button>
            </>
          )}
        </div>
        
        <div className={`text-sm text-gray-500 transition-all duration-500 delay-1100 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <p>Need help? Contact our support team at info@neuralvision.ca</p>
        </div>
      </div>
      
      {/* Animation styles */}
      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default NotFound;